import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'

import {
  getCurrentMember,
  selectCurrentMember,
  setCurrentMember,
} from '../../features/profile'
import {
  ActivityTimerManager,
  TIMER_STATUS,
  useActivityTimer,
} from '../../features/timer'
import {
  closeCoaching,
  getCoachingContextFromPath,
  openCoaching,
  selectIsCoachingOpen,
} from '../../features/coaching'
import {
  selectColorMode,
  toggleColorMode,
} from '../../features/preferences'

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded'
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded'
import Sidebar from '../../components/Sidebar'
import Footer from '../../components/Footer'
import GlobalTimerBar from '../components/GlobalTimerBar'
import { Button, PageLoadingSkeleton, SkeletonList } from '../../components/ui'

import defaultProfileImg from '../../assets/images/profile_icon.svg'
import { ReactComponent as AkiveLogo } from '../../assets/images/akive_logo.svg'
import { ROUTES } from '../../constants/routes'
import { AI_LEGAL_REQUIREMENT_EVENT } from '../../constants/events'
import { applyPosthogConsent } from '../../services/posthogConsent'
import {
  trackFeatureAction,
  trackScreenView,
} from '../../services/posthogAnalytics'
import { getCurrentLegalDocumentActionStatuses } from '../../features/legal/api/legalDocumentApi'
import { syncPosthogConsentFromServer } from '../../features/legal/api/legalDocumentService'
import AiLegalConsentDialog from '../../features/legal/components/AiLegalConsentDialog'
import {
  AI_REQUIRED_DOCUMENT_TYPES,
  areLegalDocumentTypesSatisfied,
  LEGAL_ACTION_CONTEXT,
} from '../../features/legal/lib/legalDocuments'

let aiCoachingPanelPromise

function loadAiCoachingPanel() {
  if (!aiCoachingPanelPromise) {
    aiCoachingPanelPromise = import('../../features/coaching/components/AiCoachingPanel')
  }
  return aiCoachingPanelPromise
}

function preloadAiCoachingPanel() {
  loadAiCoachingPanel().catch(() => {
    aiCoachingPanelPromise = undefined
  })
}

const AiCoachingPanel = lazy(loadAiCoachingPanel)

function AiCoachingPanelFallback({ open, onClose }) {
  if (!open) return null

  return (
    <CoachingFallbackBackdrop>
      <CoachingFallbackPanel
        id="ai-coaching-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-coaching-loading-title"
      >
        <CoachingFallbackHeader>
          <CoachingFallbackTitleGroup>
            <CoachingFallbackIcon aria-hidden="true">
              <AutoAwesomeOutlinedIcon />
            </CoachingFallbackIcon>
            <CoachingFallbackTitle id="ai-coaching-loading-title">
              AI 코칭
            </CoachingFallbackTitle>
          </CoachingFallbackTitleGroup>
          <CoachingFallbackControls>
            <CoachingFallbackPill />
            <CoachingFallbackPill />
            <CoachingFallbackPill />
            <CoachingFallbackCloseButton
              type="button"
              aria-label="AI 코칭 닫기"
              onClick={onClose}
            >
              <span aria-hidden="true">×</span>
            </CoachingFallbackCloseButton>
          </CoachingFallbackControls>
        </CoachingFallbackHeader>
        <CoachingFallbackContent>
          <CoachingFallbackChatHeader>
            <strong>AI 코칭</strong>
            <span>학습 계획, 진로 고민 등을 편하게 이야기해 보세요</span>
          </CoachingFallbackChatHeader>
          <CoachingFallbackMessages>
            <SkeletonList rows={6} label="AI 코칭 대화를 불러오는 중" />
          </CoachingFallbackMessages>
        </CoachingFallbackContent>
      </CoachingFallbackPanel>
    </CoachingFallbackBackdrop>
  )
}

function SidebarPanelToggleIcon({ expanded }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.75" y="3.75" width="18.5" height="16.5" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 4.5v15" stroke="currentColor" strokeWidth="1.5" />
      <path
        d={expanded ? 'm7 9.5-2.5 2.5L7 14.5' : 'm4.5 9.5L7 12l-2.5 2.5'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function AppShellLayout() {
  const dispatch = useDispatch()
  const location = useLocation()
  const [isSidebarHidden, setIsSidebarHidden] = useState(false)
  const [isMobileNavHidden, setIsMobileNavHidden] = useState(false)
  const [hasOpenedAiCoaching, setHasOpenedAiCoaching] = useState(false)
  const [isCheckingAiConsent, setIsCheckingAiConsent] = useState(false)
  const [showAiConsent, setShowAiConsent] = useState(false)
  const scrollContainerRef = useRef(null)
  const initialPathRef = useRef(location.pathname)
  const currentPathRef = useRef(location.pathname)
  const { status: timerStatus } = useActivityTimer()
  const currentMember = useSelector(selectCurrentMember)
  const isAiCoachingOpen = useSelector(selectIsCoachingOpen)
  const colorMode = useSelector(selectColorMode)
  const coachingContext = getCoachingContextFromPath(location.pathname)
  currentPathRef.current = location.pathname
  const handleCloseAiCoaching = useCallback(() => {
    dispatch(closeCoaching())
    if (isAiCoachingOpen) {
      trackFeatureAction({ action: 'close_panel', feature: 'coaching' })
    }
  }, [dispatch, isAiCoachingOpen])

  useLayoutEffect(() => {
    const root = document.documentElement
    const body = document.body
    const scrollElement = document.scrollingElement || root
    const previousRootOverflow = root.style.overflow
    const previousBodyOverflow = body.style.overflow

    scrollElement.scrollTop = 0
    root.scrollTop = 0
    body.scrollTop = 0
    root.style.overflow = 'hidden'
    body.style.overflow = 'hidden'

    return () => {
      root.style.overflow = previousRootOverflow
      body.style.overflow = previousBodyOverflow
      scrollElement.scrollTop = 0
      root.scrollTop = 0
      body.scrollTop = 0
    }
  }, [])

  useEffect(() => {
    if (isAiCoachingOpen) setHasOpenedAiCoaching(true)
  }, [isAiCoachingOpen])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return undefined

    let lastScrollTop = Math.max(0, scrollContainer.scrollTop)
    let accumulatedDistance = 0
    let lastDirection = 0
    let revealTimeoutId

    const revealMobileNav = () => setIsMobileNavHidden(false)
    const scheduleReveal = () => {
      window.clearTimeout(revealTimeoutId)
      revealTimeoutId = window.setTimeout(revealMobileNav, 180)
    }

    const handleScroll = () => {
      const scrollTop = Math.max(0, scrollContainer.scrollTop)
      const delta = scrollTop - lastScrollTop
      const direction = Math.sign(delta)

      if (scrollTop <= 8) {
        accumulatedDistance = 0
        revealMobileNav()
      } else if (direction !== 0) {
        if (direction !== lastDirection) accumulatedDistance = 0
        accumulatedDistance += delta

        if (accumulatedDistance > 8) setIsMobileNavHidden(true)
        if (accumulatedDistance < -6) revealMobileNav()

        lastDirection = direction
      }

      lastScrollTop = scrollTop
      scheduleReveal()
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.clearTimeout(revealTimeoutId)
      scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (initialPathRef.current === ROUTES.MY_PAGE_PRIVACY) return
    syncPosthogConsentFromServer()
      .then((enabled) => {
        if (enabled) trackScreenView(currentPathRef.current)
      })
      .catch(() => {
        // 서버 동의 상태를 확인하지 못한 동안에는 보수적으로 수집을 중단한다.
        applyPosthogConsent(false)
      })
  }, [])

  useEffect(() => {
    trackScreenView(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    const handleAiLegalRequirement = () => {
      dispatch(closeCoaching())
      setShowAiConsent(true)
    }
    window.addEventListener(AI_LEGAL_REQUIREMENT_EVENT, handleAiLegalRequirement)
    return () => window.removeEventListener(
      AI_LEGAL_REQUIREMENT_EVENT,
      handleAiLegalRequirement,
    )
  }, [dispatch])

  const handleOpenAiCoaching = async () => {
    if (isCheckingAiConsent) return
    setIsCheckingAiConsent(true)
    try {
      const result = await getCurrentLegalDocumentActionStatuses(
        LEGAL_ACTION_CONTEXT.AI_FIRST_USE,
      )
      const statuses = result.data?.legalDocumentActionStatuses || []
      if (areLegalDocumentTypesSatisfied(statuses, AI_REQUIRED_DOCUMENT_TYPES)) {
        dispatch(openCoaching())
        trackFeatureAction({ action: 'open_panel', feature: 'coaching' })
      } else {
        setShowAiConsent(true)
        trackFeatureAction({ action: 'consent_required', feature: 'coaching' })
      }
    } catch {
      setShowAiConsent(true)
      trackFeatureAction({
        action: 'consent_check_failed',
        feature: 'coaching',
        outcome: 'failure',
      })
    } finally {
      setIsCheckingAiConsent(false)
    }
  }

  const handleToggleSidebar = () => {
    trackFeatureAction({
      action: isSidebarHidden ? 'show_sidebar' : 'hide_sidebar',
      feature: 'navigation',
    })
    setIsSidebarHidden(hidden => !hidden)
  }

  useEffect(() => {
    setIsMobileNavHidden(false)
  }, [location.pathname])

  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const idleCallbackId = window.requestIdleCallback(preloadAiCoachingPanel, {
        timeout: 2000,
      })
      return () => window.cancelIdleCallback?.(idleCallbackId)
    }

    const timeoutId = window.setTimeout(preloadAiCoachingPanel, 800)
    return () => window.clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    let active = true

    getCurrentMember()
      .then((result) => {
        if (active) dispatch(setCurrentMember(result.data))
      })
      .catch(() => {
        // 각 화면이 연결 오류를 자체 상태로 안내하므로, 공통 프로필 요청의
        // 실패가 처리되지 않은 Promise로 번져 개발 오류 오버레이를 띄우지 않게 한다.
      })

    return () => { active = false }
  }, [dispatch])

  return (
    <MainContainer>
      <SkipLink href="#main-content">본문 바로가기</SkipLink>
      <Sidebar
        desktopHidden={isSidebarHidden}
        mobileHidden={isMobileNavHidden}
        profileImageUrl={currentMember?.userDetail?.profileImage?.url || defaultProfileImg}
        aim={currentMember?.userDetail?.aim || '목표와 다짐을 등록 해보세요!'}
        nickname={currentMember?.userDetail?.nickname}
      />
      <AppContentColumn>
        <AppTopBar>
          <TopBarLeading>
            <SidebarToggleButton
              type="button"
              aria-controls="desktop-sidebar"
              aria-expanded={!isSidebarHidden}
              aria-label={isSidebarHidden ? '사이드바 펼치기' : '사이드바 숨기기'}
              title={isSidebarHidden ? '사이드바 펼치기' : '사이드바 숨기기'}
              onClick={handleToggleSidebar}
            >
              <SidebarPanelToggleIcon expanded={!isSidebarHidden} />
            </SidebarToggleButton>
            <MobileBrand to={ROUTES.HOME} aria-label="akive 홈으로 이동">
              <AkiveLogo aria-hidden="true" />
            </MobileBrand>
            <WorkspaceLabel>학습의 모든 순간을 하나로</WorkspaceLabel>
          </TopBarLeading>
          <TopBarActions>
            <AiCoachingButton
              $active={isAiCoachingOpen}
              aria-label="AI 코칭"
              aria-expanded={isAiCoachingOpen}
              aria-controls="ai-coaching-panel"
              onPointerEnter={preloadAiCoachingPanel}
              onPointerDown={preloadAiCoachingPanel}
              onFocus={preloadAiCoachingPanel}
              disabled={isCheckingAiConsent}
              onClick={handleOpenAiCoaching}
            >
              <AiCoachingIcon aria-hidden="true">
                <AutoAwesomeOutlinedIcon />
              </AiCoachingIcon>
              <AiCoachingText>
                <span>AI 코치와 대화</span>
                <span>AI 코칭</span>
              </AiCoachingText>
            </AiCoachingButton>
            <TopBarActionDivider aria-hidden="true" />
            <ThemeToggleButton
              type="button"
              role="switch"
              $dark={colorMode === 'dark'}
              aria-checked={colorMode === 'dark'}
              aria-label={colorMode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
              title={colorMode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
              onClick={() => {
                dispatch(toggleColorMode())
                trackFeatureAction({
                  action: colorMode === 'dark' ? 'use_light_mode' : 'use_dark_mode',
                  feature: 'preferences',
                })
              }}
            >
              <ThemeModeIcon $active={colorMode === 'light'}>
                <LightModeRoundedIcon aria-hidden="true" />
              </ThemeModeIcon>
              <ThemeModeIcon $active={colorMode === 'dark'}>
                <DarkModeRoundedIcon aria-hidden="true" />
              </ThemeModeIcon>
            </ThemeToggleButton>
          </TopBarActions>
        </AppTopBar>
        <OtherPageWrapper
          ref={scrollContainerRef}
          $hasTimer={timerStatus !== TIMER_STATUS.IDLE}
          data-testid="app-scroll-container"
        >
          <ContentRoot id="main-content" tabIndex="-1">
            <Suspense fallback={<PageLoadingSkeleton />}>
              <Outlet/>
            </Suspense>
          </ContentRoot>
          <Footer/>
        </OtherPageWrapper>
      </AppContentColumn>
      <GlobalTimerBar />
      {(hasOpenedAiCoaching || isAiCoachingOpen) && (
        <Suspense fallback={(
          <AiCoachingPanelFallback
            open={isAiCoachingOpen}
            onClose={handleCloseAiCoaching}
          />
        )}>
          <AiCoachingPanel
            open={isAiCoachingOpen}
            onClose={handleCloseAiCoaching}
            defaultContext={coachingContext}
          />
        </Suspense>
      )}
      {showAiConsent && (
        <AiLegalConsentDialog
          onClose={() => setShowAiConsent(false)}
          onGranted={() => {
            setShowAiConsent(false)
            dispatch(openCoaching())
            trackFeatureAction({ action: 'open_panel', feature: 'coaching' })
          }}
        />
      )}
    </MainContainer>
  )
}

function AppShell() {
  return (
    <ActivityTimerManager>
      <AppShellLayout />
    </ActivityTimerManager>
  )
}

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100dvh;
  min-width: 320px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
`

const CoachingFallbackBackdrop = styled.div`
  position: fixed;
  z-index: 85;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(17, 27, 64, 0.34);
  backdrop-filter: blur(2px);
`

const CoachingFallbackPanel = styled.section`
  display: grid;
  width: min(520px, calc(100vw - 248px));
  height: 100dvh;
  grid-template-rows: auto minmax(0, 1fr);
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.medium};

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    width: 100%;
    padding-bottom: env(safe-area-inset-bottom);
  }
`

const CoachingFallbackHeader = styled.header`
  display: flex;
  min-height: 60px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};

  @media (max-width: 520px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const CoachingFallbackTitleGroup = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
`

const CoachingFallbackIcon = styled.span`
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  place-items: center;
  color: ${({ theme }) => theme.colors.primaryAccent};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};

  svg {
    width: 16px;
    height: 16px;
  }
`

const CoachingFallbackTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
`

const CoachingFallbackControls = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;

  @media (max-width: 520px) {
    width: 100%;
  }
`

const CoachingFallbackPill = styled.span`
  width: 78px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primarySoft};

  @media (max-width: 520px) {
    min-width: 0;
    flex: 1;
  }
`

const CoachingFallbackCloseButton = styled.button`
  display: grid;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  place-items: center;
  padding: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 1.6rem;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`

const CoachingFallbackContent = styled.div`
  display: flex;
  min-height: 0;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.surface};
`

const CoachingFallbackChatHeader = styled.div`
  padding: 11px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};

  strong,
  span {
    display: block;
  }

  strong {
    color: ${({ theme }) => theme.colors.text};
    font-size: 1rem;
  }

  span {
    margin-top: 2px;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 0.72rem;
  }
`

const CoachingFallbackMessages = styled.div`
  min-height: 0;
  flex: 1;
  overflow: hidden;
  padding: 32px 24px;
  background: ${({ theme }) => theme.colors.background};
`

const SkipLink = styled.a`
  position: fixed;
  z-index: 1000;
  top: 8px;
  left: 8px;
  padding: 10px 14px;
  color: ${({ theme }) => theme.colors.onPrimary};
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.primaryFill};
  transform: translateY(-160%);

  &:focus {
    transform: translateY(0);
  }
`

const AppContentColumn = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
`

const AppTopBar = styled.header`
  display: flex;
  min-height: 56px;
  align-items: center;
  justify-content: space-between;
  padding: 0 clamp(20px, 4vw, 48px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.topBar};
  backdrop-filter: blur(12px);
`

const WorkspaceLabel = styled.span`
  margin-left: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8rem;
  font-weight: 650;

  @media (max-width: 480px) { display: none; }
`

const TopBarLeading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const SidebarToggleButton = styled.button`
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  padding: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  transition: color 150ms ease, border-color 150ms ease, background 150ms ease;

  svg { width: 21px; height: 21px; }

  &:hover, &:focus-visible {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    display: none;
  }
`

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
`

const TopBarActionDivider = styled.span`
  width: 1px;
  height: 22px;
  flex: 0 0 1px;
  background: ${({ theme }) => theme.colors.borderStrong};
`

const ThemeToggleButton = styled.button`
  position: relative;
  display: inline-flex;
  width: 64px;
  height: 32px;
  flex: 0 0 64px;
  align-items: center;
  justify-content: space-between;
  padding: 0 3px;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primarySoft};
  transition: border-color 150ms ease, background 150ms ease;

  &::before {
    position: absolute;
    z-index: 0;
    top: 2px;
    left: 2px;
    width: 26px;
    height: 26px;
    content: '';
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.surface};
    box-shadow: ${({ theme }) => theme.shadow.low};
    transform: translateX(${({ $dark }) => $dark ? '32px' : '0'});
    transition: transform 180ms ease, background 150ms ease;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primaryAccent};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.focusRing};
    outline-offset: 2px;
  }

  @media (max-width: 420px) {
    width: 58px;
    height: 30px;
    flex-basis: 58px;

    &::before {
      width: 24px;
      height: 24px;
      transform: translateX(${({ $dark }) => $dark ? '28px' : '0'});
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      transition: none;
    }
  }
`

const ThemeModeIcon = styled.span`
  position: relative;
  z-index: 1;
  display: inline-grid;
  width: 28px;
  height: 28px;
  place-items: center;
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textMuted};
  opacity: ${({ $active }) => $active ? 1 : 0.58};
  transition: color 150ms ease, opacity 150ms ease;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 420px) {
    width: 25px;
    height: 25px;

    svg {
      width: 15px;
      height: 15px;
    }
  }
`

const flowBlue = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

const AiCoachingButton = styled(Button)`
  min-height: 34px;
  gap: 7px;
  padding: 4px 13px 4px 5px;
  color: ${({ theme }) => theme.colors.primary};
  border-color: ${({ $active }) =>
    $active ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.25)'};
  border-radius: 18px 18px 18px 7px;
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(135deg, #202A43 0%, #263B55 40%, #293957 70%, #202F48 100%)'
    : 'linear-gradient(135deg, #EEF2FF 0%, #DBEAFE 40%, #E0E7FF 70%, #F0F9FF 100%)'};
  background-size: 200% 200%;
  box-shadow: 0 2px 10px rgba(186, 204, 255, 0.3);
  animation: ${flowBlue} 5s ease-in-out infinite;
  transition: box-shadow 300ms ease, transform 150ms ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ $active }) =>
      $active ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.25)'};
    background: ${({ theme }) => theme.mode === 'dark'
      ? 'linear-gradient(135deg, #202A43 0%, #263B55 40%, #293957 70%, #202F48 100%)'
      : 'linear-gradient(135deg, #EEF2FF 0%, #DBEAFE 40%, #E0E7FF 70%, #F0F9FF 100%)'};
    background-size: 200% 200%;
    box-shadow: 0 4px 15px rgba(186, 204, 255, 0.5);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    filter: brightness(0.95);
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.focusSoft};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const AiCoachingIcon = styled.span`
  display: grid;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  place-items: center;
  color: ${({ theme }) => theme.colors.primaryAccent};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};
  transition: color 150ms ease, background 150ms ease;

  svg {
    width: 14px;
    height: 14px;
  }

  ${AiCoachingButton}[aria-expanded='true'] & {
    color: #FFFFFF;
    background: ${({ theme }) => theme.colors.primaryAccent};
  }
`

const AiCoachingText = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: -0.015em;

  > span:last-child {
    display: none;
  }

  @media (max-width: 420px) {
    > span:first-child {
      display: none;
    }

    > span:last-child {
      display: inline;
    }
  }
`

const MobileBrand = styled(Link)`
  display: none;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.small};

  svg {
    display: block;
    width: 92px;
    height: 28px;
    shape-rendering: geometricPrecision;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    display: inline-flex;
    align-items: center;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.focusRing};
    outline-offset: 3px;
  }
`

const OtherPageWrapper = styled.div`
  min-width: 0;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  scrollbar-gutter: stable;
  padding-bottom: ${({ $hasTimer }) => ($hasTimer ? '96px' : '0')};

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    padding-bottom: ${({ $hasTimer }) => $hasTimer
      ? 'calc(162px + env(safe-area-inset-bottom))'
      : 'calc(66px + env(safe-area-inset-bottom))'};
    scrollbar-gutter: auto;
  }
`

const ContentRoot = styled.main`
  min-width: 0;
  min-height: calc(100dvh - 56px - 124px);

  &:focus {
    outline: none;
  }
`

export default AppShell
